import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  detectDevice,
  createDeepLinkUrl,
  createWebFlowUrl,
  attemptAppLaunch,
  logPairingCodeSafely,
} from './helpers';
import type { InvitationRouterState } from './types';
import { InvitationStatus, DeviceType } from './types';

const COUNTDOWN_DURATION = 3; // seconds
const CLEANUP_DELAY = 5000; // milliseconds

interface UseInvitationRouterProps {
  pairingCode: string | undefined;
}

interface UseInvitationRouterReturn extends InvitationRouterState {
  handleContinueInBrowser: () => void;
}

/**
 * Custom hook that manages the invitation router logic
 */
export const useInvitationRouter = ({
  pairingCode,
}: UseInvitationRouterProps): UseInvitationRouterReturn => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<InvitationStatus>(
    InvitationStatus.DETECTING
  );
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);

  /**
   * Routes to web flow
   */
  const routeToWebFlow = useCallback(
    (pairingCode: string) => {
      logPairingCodeSafely(
        pairingCode,
        'Routing to web flow with pairing code'
      );
      const webFlowUrl = createWebFlowUrl(pairingCode);
      navigate(webFlowUrl);
    },
    [navigate]
  );

  /**
   * Handles the "Continue in browser" button click
   */
  const handleContinueInBrowser = useCallback(() => {
    if (pairingCode) {
      routeToWebFlow(pairingCode);
    }
  }, [pairingCode, routeToWebFlow]);

  /**
   * Attempts to launch the mobile app
   */
  const attemptMobileAppLaunch = useCallback(
    async (pairingCode: string) => {
      logPairingCodeSafely(
        pairingCode,
        'Attempting to launch mobile app with pairing code'
      );

      const deepLinkUrl = createDeepLinkUrl(pairingCode);

      // Start countdown
      let timeLeft = COUNTDOWN_DURATION;
      setCountdown(timeLeft);

      const countdownInterval = setInterval(() => {
        timeLeft--;
        setCountdown(timeLeft);

        if (timeLeft <= 0) {
          clearInterval(countdownInterval);
        }
      }, 1000);

      try {
        const appOpened = await attemptAppLaunch(deepLinkUrl);

        clearInterval(countdownInterval);

        if (!appOpened) {
          // App did not open, falling back to web flow
          setStatus(InvitationStatus.FALLBACK);

          // Give user a moment to see the fallback message
          setTimeout(() => {
            routeToWebFlow(pairingCode);
          }, 1500);
        }
      } catch (_error) {
        // Failed to launch app
        clearInterval(countdownInterval);
        routeToWebFlow(pairingCode);
      }

      // Cleanup after delay regardless of outcome
      setTimeout(() => {
        clearInterval(countdownInterval);
      }, CLEANUP_DELAY);
    },
    [routeToWebFlow]
  );

  /**
   * Main detection and routing logic
   */
  const detectAndRoute = useCallback(
    async (pairingCode: string) => {
      setStatus(InvitationStatus.DETECTING);

      const deviceInfo = detectDevice();

      // Log device detection for debugging
      console.info('Device detection:', {
        isMobile: deviceInfo.isMobile,
        isTablet: deviceInfo.isTablet,
        deviceType: deviceInfo.deviceType,
        userAgent: navigator.userAgent,
      });

      if (deviceInfo.deviceType === DeviceType.MOBILE) {
        // Mobile device - try deep link first
        setStatus(InvitationStatus.ROUTING);
        await attemptMobileAppLaunch(pairingCode);
      } else {
        // Desktop or tablet - go directly to web flow
        routeToWebFlow(pairingCode);
      }
    },
    [attemptMobileAppLaunch, routeToWebFlow]
  );

  /**
   * Effect to handle initial routing
   */
  useEffect(() => {
    if (!pairingCode) {
      // No pairing code provided, redirect to home or error page
      navigate('/');

      return;
    }

    // Route once the pairing code is present.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    detectAndRoute(pairingCode);
  }, [pairingCode, navigate, detectAndRoute]);

  return {
    status,
    countdown,
    handleContinueInBrowser,
  };
};
